/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { z } from 'zod';

/**
 * 12+ chars, at least one uppercase, one lowercase, one digit, one symbol.
 * Mirrors `BCRYPT_ROUNDS` and `PASSWORD_MIN_LENGTH` from `.env.example` and
 * matches iTech §5.1 (password policy).
 */
export const PasswordSchema = z
  .string()
  .min(12, 'Password must be at least 12 characters long')
  .refine((v) => /[A-Z]/.test(v), 'Password must contain an uppercase letter')
  .refine((v) => /[a-z]/.test(v), 'Password must contain a lowercase letter')
  .refine((v) => /\d/.test(v), 'Password must contain a digit')
  .refine((v) => /[^A-Za-z0-9]/.test(v), 'Password must contain a symbol');

/** RFC 5321-compliant email, normalised to lower case. */
export const EmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email('Enter a valid email address')
  .max(254, 'Email is too long');

/** 6-digit TOTP code as accepted by `otplib`. */
export const TotpCodeSchema = z
  .string()
  .regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator app');

/**
 * Tenant identifier — UUID v4 in production. Validated as a UUID at every
 * boundary so a malformed identifier never reaches Drizzle.
 */
export const ClinicIdSchema = z.string().uuid('Invalid clinic identifier');

/** Hex-encoded password-reset token — exactly 64 hex chars (sha256 length). */
export const PasswordResetTokenSchema = z.string().regex(/^[0-9a-f]{64}$/, 'Invalid reset token');
